import { useTranslation } from "react-i18next";
import { Flex } from "../../../../../components/Flex";
import { AtiraIcon } from "../../../../../components/AtiraIcon";
import styled from "styled-components";
import { Spacing } from "../../../../../theme/Spacing";
import { FontSize, Text } from "../../../../../components/Text";
import { useAppSelector } from "../../../../../redux/store";
import { reportSliceSelectors } from "../../../../../redux/report/report.selector";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { Info } from "../../../../../components/Info";
import { useMemo, useState } from "react";
import { ReportsUserDrawer } from "../../ReportsUserDrawer";
import { AtiraTable } from "../../../../../components/table/AtiraTable";
import { createPerUserSummaryReportsTableColumns } from "../../../utils/createPerUserSummaryReportsTableColumns";

const Title = styled(Text)`
  display: flex;
  align-items: center;
  gap: ${Spacing.s};
  font-weight: bold;
  font-size: ${FontSize.l};
  color: ${({ theme }) => theme.main};

  svg {
    width: 2.5rem;
  }
`;

export const ReportsSummaryUserTab: React.FC = () => {
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { t } = useTranslation();

  const reports = useAppSelector(reportSliceSelectors.selectReport);

  // The report carries each member's name and picture, so the table no longer
  // needs a separate members fetch to render a row.
  const rows = useMemo(() => reports.metricsByUser || [], [reports]);

  const userDrawerData = reports?.metricsDataCentric?.find(
    (m) => m.userId === selectedMemberId,
  );

  const selectedMemberRow = rows.find(
    (row) => row.userId === selectedMemberId,
  );

  const columns = useMemo(
    () =>
      createPerUserSummaryReportsTableColumns(
        ["actions", "assignee", "entries", "tasks", "deals"],
        {
          t,
          onDetails: (data) => {
            setSelectedMemberId(data.userId);
            setIsDrawerOpen(true);
          },
        },
      ),
    [t],
  );

  return (
    <Flex flexDirection="column" gap="s">
      <Title>
        <AtiraIcon icon={faUser} />
        {t("common.users")}
      </Title>

      <Flex flexDirection="column" gap="l">
        <Flex flexDirection="column" gap="s">
          <Info info={t("reports.tabs.summary.subtab.user.info")} />

          <AtiraTable<any>
            data={rows}
            rowKey={(d) => d.userId}
            columns={columns}
            pagination={false}
          />
        </Flex>
      </Flex>

      <ReportsUserDrawer
        open={isDrawerOpen}
        setOpen={setIsDrawerOpen}
        data={userDrawerData}
        metrics={
          selectedMemberRow
            ? {
                dealsClosed: selectedMemberRow.dealsClosed,
                tasksDone: selectedMemberRow.tasksDone,
                entriesConverted: selectedMemberRow.entriesConverted,
              }
            : undefined
        }
      />
    </Flex>
  );
};
